import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { SortUsersBy } from '../../../enum/user-management/sort-users-by.enum';
import { SortUsersDirection } from '../../../enum/user-management/sort-users-direction.enum';
import { FilterUsersDirectCare } from '../../../enum/user-management/filter-users-direct-care.enum';
import { FilterUsersActiveToday } from '../../../enum/user-management/filter-users-active-today.enum';
import { UsersPageStore } from './users-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FilterBuilder } from '../../../helper/filter.builder';
import { InfiniteScrollStore } from '../../../../shared/store/common/infinite-scroll.store';
import { getUsersFrag } from './users-page.query';
import { fb } from '../../../../shared/shared.global-service';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserGridItemObject } from '@it2go/types';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { HeaderComponent } from '../../../../shared/components/kit/header/header.component';
import {
    InfiniteScrollLoadOnScreenComponent,
} from '../../../../../components/kit/infinite-scroll/infinite-scroll-load-on-screen/infinite-scroll-load-on-screen.component';
import { UserCardComponent } from '../../../components/user-management/user-card/user-card.component';
import { UsersFiltersComponent } from '../../../components/user-management/users-filters/users-filters.component';
import {
    UsersHeaderButtonsComponent,
} from '../../../components/user-management/users-header-buttons/users-header-buttons.component';

@Component({
    selector: 'app-users-page',
    templateUrl: './users-page.component.html',
    styleUrls: ['./users-page.component.scss'],
    providers: [UsersPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        InfiniteScrollLoadOnScreenComponent,
        UserCardComponent,
        UsersFiltersComponent,
        UsersHeaderButtonsComponent,
    ],
})
export class UsersPageComponent extends SubscriptionComponent {

    public readonly store = inject(UsersPageStore);

    protected filteringForm = fb.group({
        showDeactivated: fb.control<boolean>(false),
        allUsers: fb.control<boolean>(false),
        selectedService: fb.control<number | null>(null),
        searchedText: fb.control<string | null>(null),
    });

    protected sortingForm = fb.group({
        sortBy: fb.control<SortUsersBy>(SortUsersBy.NoSort),
        sortDirection: fb.control<SortUsersDirection>(SortUsersDirection.ASC),
        filterDirectCare: fb.control<FilterUsersDirectCare>(FilterUsersDirectCare.NoMatter),
        filterActiveToday: fb.control<FilterUsersActiveToday>(FilterUsersActiveToday.NoMatter),
    });

    protected sortChange = toSignal(this.sortingForm.valueChanges);
    protected filterChange = toSignal(this.filteringForm.valueChanges);
    protected serviceChange = toSignal(this.filteringForm.controls.selectedService.valueChanges);
    protected forceUpdate = signal(0);

    protected userScroll = computed(() => {
        this.forceUpdate();
        this.sortChange();
        this.filterChange();
        const builder = new FilterBuilder();
        builder.limit(50);
        this.createFilters(builder);
        this.createSorters(builder);

        return new InfiniteScrollStore<UserGridItemObject>(
            this.store.api.getUsers,
            builder.filter(),
            true,
            getUsersFrag,
        );
    });

    constructor() {
        super();
        effect(() => {
            this.store.globalGlobalStore.loggedUserStore.setSelectedServiceId(this.serviceChange(), false).then();
        });
        this.store.onSuccess(
            () => {
                this.forceUpdate.update((acc) => acc + 1);
            },
            'createUser',
            'updateUser',
            'deleteUser',
        );
    }

    private createFilters(builder: FilterBuilder): void {
        // TODO: filtering direct care and today's activity
        const showDeactivated: boolean = this.filteringForm.value.showDeactivated ?? false;
        const searchedText: string | null = this.filteringForm.value.searchedText ?? null;

        if (!showDeactivated) {
            builder.where('active', true);
        }
        if (this.filteringForm.value.allUsers !== true) {
            builder.where('systemic', true);
        }
        builder.organizationId(this.store.globalGlobalStore.organizationStore.selectedId());
        builder.search(searchedText);

        const organizationUsesServices = this.store.globalGlobalStore.organizationStore.isSettingActive(OrganizationSettingTypeEnum.HEALTHCARE_SOCIAL_SETTINGS)();
        if (organizationUsesServices) {
            // TODO services must be hidden behind feature flag
            const service = this.filteringForm.value.selectedService;
            if (service) {
                builder.where('serviceId', service);
            } else {
                const globallyAllowed = this.store.isAllowedGlobal('user.read')();
                if (!globallyAllowed) {
                    const serviceIds = this.store.globalGlobalStore.organizationStore.services().map((it) => it.id);
                    if (!serviceIds.length) serviceIds.push(0);
                    builder.where('serviceId', serviceIds, 'IN');
                }
            }
        }
    }

    private createSorters(builder: FilterBuilder): void {
        // TODO: sorting by reportedWork and working time
        const sortBy: SortUsersBy = this.sortingForm.value.sortBy ?? SortUsersBy.NoSort;
        const sortDirection: 'ASC' | 'DESC' = (this.sortingForm.value.sortDirection ?? SortUsersDirection.ASC) === SortUsersDirection.ASC ? 'ASC' : 'DESC';

        if (sortBy === SortUsersBy.SurnameAndName) {
            builder.sort('surname', sortDirection);
            builder.sort('name', sortDirection);
        }
    }
}
